import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Card, IconButton, Divider, Icon, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Bar, Doughnut } from "react-chartjs-2";
import { request } from "app/network";
// import colors from "assets/theme/base/colors"; // Assuming access to theme

// Interfaces
interface HourlyAdPerformanceDTO {
    hour: number;
    hourStr: string;
    views: number;
    clicks: number;
}

interface DeviceDistributionDTO {
    category: string;
    count: number;
    percentage: number;
}

interface AdPerformanceSummaryDTO {
    totalViews: number;
    totalClicks: number;
    avgCtr: number;
    avgCompletionRate: number;
}

interface AdDetailStatsDTO {
    seqId: number;
    title: string;
    imageUrl: string;
    periodStr: string;
    status: string;
    statusLabel: string;
    avgWatchTime: number;
    summary: AdPerformanceSummaryDTO;
    hourlyTrends: HourlyAdPerformanceDTO[];
    deviceDistribution: DeviceDistributionDTO[];
}

interface AdDetailDialogProps {
    open: boolean;
    onClose: () => void;
    seqId: number | null;
    startDate?: Date;
    endDate?: Date;
}

const AdDetailDialog = ({ open, onClose, seqId, startDate, endDate }: AdDetailDialogProps) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<AdDetailStatsDTO | null>(null);

    useEffect(() => {
        if (open && seqId) {
            fetchData();
        } else {
            setData(null);
        }
    }, [open, seqId]);

    const fetchData = async () => {
        if (!seqId) return;
        setLoading(true);
        try {
            const response = await request("InterstitialAd.getDetailStats", {
                paramz: {
                    seqId,
                    startDate: startDate ? startDate.toISOString() : undefined,
                    endDate: endDate ? endDate.toISOString() : undefined
                }
            });
            if (response && response.data) {
                setData(response.data);
            }
        } catch (error) {
            console.error(error);
            // alert("Failed to fetch ad details");
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    // Chart Data Preparation
    const hourlyLabels = data?.hourlyTrends.map(x => x.hourStr) || [];
    const hourlyViews = data?.hourlyTrends.map(x => x.views) || [];
    const hourlyClicks = data?.hourlyTrends.map(x => x.clicks) || [];

    const hourlyChartData = {
        labels: hourlyLabels,
        datasets: [
            {
                type: 'bar' as const,
                label: "노출 (Impressions)",
                data: hourlyViews,
                backgroundColor: 'rgba(54, 162, 235, 0.7)', // Blue
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 0,
                borderRadius: 4,
                barPercentage: 0.6,
                order: 2,
                yAxisID: 'y',
            },
            {
                type: 'line' as const,
                label: "클릭 (Clicks)",
                data: hourlyClicks,
                borderColor: '#FF7043',
                backgroundColor: 'rgba(255, 112, 67, 0.2)',
                borderWidth: 3,
                pointRadius: 4,
                pointBackgroundColor: '#fff',
                pointBorderColor: '#FF7043',
                pointHoverRadius: 6,
                fill: true,
                tension: 0.4,
                order: 1,
                yAxisID: 'y1',
            }
        ]
    };

    const deviceLabels = data?.deviceDistribution.map(x => x.category) || [];
    const deviceCounts = data?.deviceDistribution.map(x => x.count) || [];
    const deviceChartData = {
        labels: deviceLabels,
        datasets: [
            {
                data: deviceCounts,
                backgroundColor: ["#344767", "#7b809a", "#e9ecef", "#dee2e6"], // Android (Navy), iOS (Grey), Others (Light Greys)
                hoverOffset: 6,
                borderColor: "#ffffff",
                borderWidth: 2
            }
        ]
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="h6">광고 상세 분석</MDTypography>
                    <Icon onClick={onClose} sx={{ cursor: "pointer" }}>close</Icon>
                </MDBox>
            </DialogTitle>
            <Divider />
            <DialogContent>
                {loading ? (
                    <MDBox display="flex" justifyContent="center" p={5}>
                        <CircularProgress color="info" />
                    </MDBox>
                ) : data ? (
                    <MDBox>
                        <Grid container spacing={3} mb={3}>
                            {/* Left Column: Vertical Image */}
                            <Grid item xs={12} md={3} lg={2.5}>
                                <Card sx={{ p: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <MDBox
                                        component="img"
                                        src={data.imageUrl}
                                        alt={data.title}
                                        width="100%"
                                        height="100%"
                                        borderRadius="lg"
                                        sx={{ objectFit: 'contain', maxHeight: '250px', minHeight: '180px' }}
                                    />
                                </Card>
                            </Grid>

                            {/* Right Column: Header Info + Cards */}
                            <Grid item xs={12} md={9} lg={9.5}>
                                <MDBox display="flex" flexDirection="column" height="100%">
                                    {/* Header Info */}
                                    <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="flex-start">
                                        <MDBox>
                                            <MDTypography variant="h3" fontWeight="bold" mb={1}>
                                                {data.title}
                                            </MDTypography>
                                            <MDBox display="flex" alignItems="center">
                                                <Icon fontSize="small" sx={{ color: '#7b809a', mr: 0.5 }}>calendar_today</Icon>
                                                <MDTypography variant="body2" color="text">
                                                    {data.periodStr}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                        <MDBox
                                            px={2}
                                            py={0.5}
                                            borderRadius="lg"
                                            sx={{
                                                background: data.status === "Active" || data.status === "ACTIVE"
                                                    ? "#4CAF50" // Solid Green
                                                    : (data.status === "Scheduled" ? "#FF9800" : "#9E9E9E"),
                                                color: "#fff",
                                                boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
                                            }}
                                        >
                                            <MDTypography variant="button" color="white" fontWeight="bold">
                                                {data.statusLabel}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>

                                    {/* Summary Cards Grid - Bottom Aligned */}
                                    <MDBox mt="auto">
                                        <Grid container spacing={2}>
                                            {/* Impressions - Blue Gradient (Match AdStatsScreen) */}
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Card sx={{
                                                    p: 2.5,
                                                    background: "linear-gradient(135deg, #42A5F5 0%, #1E88E5 100%)",
                                                    color: "#fff",
                                                    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.14)",
                                                    height: "100%",
                                                    position: "relative",
                                                    overflow: "hidden"
                                                }}>
                                                    <MDBox display="flex" flexDirection="column" height="100%">
                                                        <Icon fontSize="medium" style={{ color: "#ffffff" }} sx={{ mb: 1 }}>visibility</Icon>
                                                        <MDTypography variant="caption" color="white" opacity={0.9}>총 노출 수</MDTypography>
                                                        <MDTypography variant="h4" color="white" fontWeight="bold">
                                                            {data.summary.totalViews.toLocaleString()}
                                                        </MDTypography>
                                                    </MDBox>
                                                </Card>
                                            </Grid>

                                            {/* Clicks - Orange Gradient (Match AdStatsScreen) */}
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Card sx={{
                                                    p: 2.5,
                                                    background: "linear-gradient(135deg, #FF8A65 0%, #FF5722 100%)",
                                                    color: "#fff",
                                                    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.14)",
                                                    height: "100%",
                                                    position: "relative",
                                                    overflow: "hidden"
                                                }}>
                                                    <MDBox display="flex" flexDirection="column" height="100%">
                                                        <Icon fontSize="medium" style={{ color: "#ffffff" }} sx={{ mb: 1 }}>touch_app</Icon>
                                                        <MDTypography variant="caption" color="white" opacity={0.9}>총 클릭 수</MDTypography>
                                                        <MDTypography variant="h4" color="white" fontWeight="bold">
                                                            {data.summary.totalClicks.toLocaleString()}
                                                        </MDTypography>
                                                    </MDBox>
                                                </Card>
                                            </Grid>

                                            {/* CTR - Green Gradient (Match AdStatsScreen) */}
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Card sx={{
                                                    p: 2.5,
                                                    background: "linear-gradient(135deg, #66BB6A 0%, #43A047 100%)",
                                                    color: "#fff",
                                                    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.14)",
                                                    height: "100%",
                                                    position: "relative",
                                                    overflow: "hidden"
                                                }}>
                                                    <MDBox display="flex" flexDirection="column" height="100%">
                                                        <Icon fontSize="medium" style={{ color: "#ffffff" }} sx={{ mb: 1 }}>analytics</Icon>
                                                        <MDTypography variant="caption" color="white" opacity={0.9}>평균 클릭률</MDTypography>
                                                        <MDTypography variant="h4" color="white" fontWeight="bold">
                                                            {data.summary.avgCtr}%
                                                        </MDTypography>
                                                    </MDBox>
                                                </Card>
                                            </Grid>

                                            {/* Completion Rate - Purple Gradient (Match AdStatsScreen) */}
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Card sx={{
                                                    p: 2.5,
                                                    background: "linear-gradient(135deg, #AB47BC 0%, #8E24AA 100%)",
                                                    color: "#fff",
                                                    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.14)",
                                                    height: "100%",
                                                    position: "relative",
                                                    overflow: "hidden"
                                                }}>
                                                    <MDBox display="flex" flexDirection="column" height="100%">
                                                        <Icon fontSize="medium" style={{ color: "#ffffff" }} sx={{ mb: 1 }}>play_circle_filled</Icon>
                                                        <MDTypography variant="caption" color="white" opacity={0.9}>광고 완료율</MDTypography>
                                                        <MDTypography variant="h4" color="white" fontWeight="bold">
                                                            {data.summary.avgCompletionRate}%
                                                        </MDTypography>
                                                    </MDBox>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>

                        <Grid container spacing={3}>
                            {/* Hourly Trend Chart */}
                            <Grid item xs={12} md={8}>
                                <Card sx={{ p: 3, height: '100%' }}>
                                    <MDBox display="flex" alignItems="center" mb={2}>
                                        <Icon fontSize="medium" sx={{ mr: 1, color: "#344767" }}>access_time</Icon>
                                        <MDTypography variant="h6" fontWeight="bold" color="text">
                                            시간대별 노출/클릭
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox height="300px">
                                        <Bar
                                            data={hourlyChartData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                interaction: {
                                                    mode: 'index' as const,
                                                    intersect: false,
                                                },
                                                plugins: {
                                                    legend: {
                                                        position: 'bottom' as const,
                                                        labels: {
                                                            usePointStyle: true,
                                                            pointStyle: 'circle',
                                                            color: '#344767',
                                                            padding: 20,
                                                            font: {
                                                                family: 'Inter',
                                                                size: 12
                                                            }
                                                        }
                                                    },
                                                    tooltip: {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                        padding: 12,
                                                        titleFont: { size: 13 },
                                                        bodyFont: { size: 13 },
                                                        displayColors: true,
                                                        intersect: false,
                                                        mode: 'index' as const,
                                                    }
                                                },
                                                scales: {
                                                    y: {
                                                        type: 'linear' as const,
                                                        display: true,
                                                        position: 'left' as const,
                                                        grid: {
                                                            drawBorder: false,
                                                            display: true,
                                                            drawOnChartArea: true,
                                                            drawTicks: false,
                                                            borderDash: [5, 5],
                                                            color: 'rgba(0, 0, 0, 0.1)'
                                                        },
                                                        ticks: {
                                                            padding: 10,
                                                            color: '#7b809a',
                                                            font: {
                                                                size: 11,
                                                                family: "Pretendard"
                                                            },
                                                        },
                                                        title: {
                                                            display: true,
                                                            text: '노출',
                                                            color: '#7b809a',
                                                            font: { size: 10 }
                                                        }
                                                    },
                                                    y1: {
                                                        type: 'linear' as const,
                                                        display: true,
                                                        position: 'right' as const,
                                                        grid: {
                                                            drawOnChartArea: false, // only want the grid lines for one axis to show up
                                                        },
                                                        ticks: {
                                                            padding: 10,
                                                            color: '#FF7043',
                                                            font: {
                                                                size: 11,
                                                                family: "Pretendard"
                                                            },
                                                        }
                                                    },
                                                    x: {
                                                        grid: {
                                                            drawBorder: false,
                                                            display: false,
                                                            drawOnChartArea: false,
                                                            drawTicks: false,
                                                            borderDash: [5, 5]
                                                        },
                                                        ticks: {
                                                            display: true,
                                                            color: '#7b809a',
                                                            padding: 10,
                                                            font: {
                                                                size: 11,
                                                                family: "Pretendard"
                                                            },
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>

                            {/* Device Distribution Chart */}
                            <Grid item xs={12} md={4}>
                                <Card sx={{ p: 3, height: '100%' }}>
                                    <MDBox display="flex" alignItems="center" mb={2}>
                                        <Icon fontSize="medium" sx={{ mr: 1, color: "#344767" }}>smartphone</Icon>
                                        <MDTypography variant="h6" fontWeight="bold" color="text">
                                            디바이스 분포
                                        </MDTypography>
                                    </MDBox>

                                    <MDBox height="250px" display="flex" justifyContent="center" position="relative">
                                        <Doughnut
                                            data={deviceChartData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        display: false
                                                    }
                                                },
                                                cutout: "60%"
                                            }}
                                        />
                                        {/* Center Text for Doughnut */}
                                        <MDBox
                                            position="absolute"
                                            top="50%"
                                            left="50%"
                                            sx={{ transform: "translate(-50%, -50%)", textAlign: "center" }}
                                        >
                                            <MDTypography variant="h6" fontWeight="bold">
                                                {data.deviceDistribution.find(d => d.category === "Android")?.percentage || 0}%
                                            </MDTypography>
                                            <MDTypography variant="caption" color="text">
                                                Android
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>

                                    {/* Custom Legend */}
                                    <MDBox mt={3}>
                                        {data.deviceDistribution.map((d, i) => (
                                            <MDBox key={i} display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                                <MDBox display="flex" alignItems="center">
                                                    <MDBox
                                                        width={12}
                                                        height={12}
                                                        borderRadius="50%"
                                                        bgcolor={deviceChartData.datasets[0].backgroundColor[i]}
                                                        mr={1}
                                                    />
                                                    <MDTypography variant="button" color="text">{d.category}</MDTypography>
                                                </MDBox>
                                                <MDBox display="flex" alignItems="center">
                                                    <MDTypography variant="button" fontWeight="bold" mr={1}>{d.percentage}%</MDTypography>
                                                    <MDTypography variant="caption" color="text">({d.count.toLocaleString()})</MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        ))}
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                    </MDBox>
                ) : (
                    <MDTypography align="center" mt={3}>데이터가 없습니다.</MDTypography>
                )}
            </DialogContent>

        </Dialog>
    );
};

export default AdDetailDialog;
